import React from 'react';
import { Box, Container, Typography, Stack, Divider, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import Header from './Header';
import SEO from './SEO';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GroupsIcon from '@mui/icons-material/Groups';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VerifiedIcon from '@mui/icons-material/Verified';

const values = [
  {
    icon: <FavoriteIcon sx={{ color: '#ff6b6b', fontSize: 22 }} />,
    title: "We're parents first.",
    body: "Every person who built SMCLA has young children. This is not a market opportunity to us. It is the thing we think about when we drop our kids off in the morning and when we pick them up at the end of the day. We built the product we wish existed.",
    accent: 'rgba(255,107,107,0.08)',
    border: 'rgba(255,107,107,0.15)',
  },
  {
    icon: <ShieldIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: "Prevention over documentation.",
    body: "Most surveillance tools produce evidence after harm occurs. We built SMCLA to stop harm before it compounds. The goal is never to have footage — it is to never need it. Real-time detection is not a feature. It is the entire point.",
    accent: 'rgba(92,225,230,0.08)',
    border: 'rgba(92,225,230,0.15)',
  },
  {
    icon: <VisibilityOffIcon sx={{ color: '#8c52ff', fontSize: 22 }} />,
    title: "Privacy is not optional.",
    body: "We believe in the dignity of the workers who do this difficult job honestly. Surveillance that protects children must also respect adults. SMCLA is built on data minimization, role-based access, and automatic deletion — not bulk monitoring. Every design decision reflects that.",
    accent: 'rgba(140,82,255,0.08)',
    border: 'rgba(140,82,255,0.15)',
  },
  {
    icon: <GroupsIcon sx={{ color: '#ff9f43', fontSize: 22 }} />,
    title: "Workers deserve protection too.",
    body: "Good caregivers are falsely accused. Timestamped, tamper-evident records protect the innocent as much as they expose the guilty. SMCLA is not a tool for punishing staff — it is a tool for putting the truth on the record.",
    accent: 'rgba(255,159,67,0.08)',
    border: 'rgba(255,159,67,0.15)',
  },
  {
    icon: <LightbulbIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: "We take the hard problems seriously.",
    body: "Emotional abuse, attention-seeking behavior, behavioral escalation patterns — these are the cases that existing technology ignores because they are harder to detect. We spent two years on them precisely because they are harder. The easy problems already have solutions.",
    accent: 'rgba(92,225,230,0.08)',
    border: 'rgba(92,225,230,0.15)',
  },
  {
    icon: <VerifiedIcon sx={{ color: '#ff9f43', fontSize: 22 }} />,
    title: "We move carefully.",
    body: "We are a small team deploying technology in high-stakes environments involving children. We do not ship fast and fix later. Every alert threshold, every data retention policy, every compliance decision is tested against the standard of: what if this were my child?",
    accent: 'rgba(255,159,67,0.08)',
    border: 'rgba(255,159,67,0.15)',
  },
];

const timeline = [
  {
    year: '2023',
    title: 'The question that started it',
    body: 'Two of our founders — both new parents — started asking the same question independently: why does childcare surveillance look exactly like retail loss prevention? The technology existed. No one had applied it to the right problem.',
  },
  {
    year: '2023–2024',
    title: 'Two years of research',
    body: 'We spent a year studying abuse cases, reading court documents, speaking with daycare operators, parents, licensing boards, and child safety attorneys. We built our detection model around real documented patterns — not hypothetical scenarios.',
  },
  {
    year: '2024',
    title: 'First detection model',
    body: "Our first behavioral AI model was trained on annotated childcare footage. We tested obsessively: false positive rates, edge cases, emotional abuse audio signatures, the difference between normal rough play and an assault. We didn't launch until the model was right.",
  },
  {
    year: '2024–2025',
    title: 'Privacy architecture',
    body: 'We built the privacy layer before we built the product layer. Data minimization, auto-deletion windows, role-based access, chain-of-custody metadata, state-by-state audio consent compliance. These were not retrofitted. They are structural.',
  },
  {
    year: '2025',
    title: 'Pilot program launched',
    body: 'Limited rollout with founding daycare partners. Real facilities, real staff, real children. We ran the system, listened to operators, and refined the alert logic based on what mattered in practice — not just in theory.',
  },
  {
    year: '2025–present',
    title: 'Growing carefully',
    body: "We are still a startup. We are still small. We are adding facilities deliberately, not at scale-for-scale's-sake. Every new site is an environment where real children spend their days. We treat it that way.",
  },
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="About SMCLA — Built to Protect Children" description="The story behind Smart Media Clarity — why we built behavioral AI specifically for childcare, and the mission that drives every feature." path="/about" />
      <Header onBookDemo={() => navigate('/contact')} />

      {/* Hero */}
      <Box sx={{ position: 'relative', overflow: 'hidden', pt: { xs: 10, md: 14 }, pb: { xs: 8, md: 12 }, px: 3 }}>
        <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.1) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-10%', left: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(140,82,255,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
            Who We Are
          </Typography>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.5rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', lineHeight: 1.05, mb: 3, maxWidth: 700 }}>
            We built SMCLA because we are parents.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>That changes everything.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 19 }, color: '#9499b0', lineHeight: 1.7, maxWidth: 580, mb: 2 }}>
            Smart Media Clarity is a startup with a two-year head start on a problem that the childcare industry has avoided for decades. We are a small team. We have young children. We believe that the technology to protect them has always existed — it just needed to be built for the right purpose.
          </Typography>
        </Container>
      </Box>

      {/* Origin story */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
                Why We Started
              </Typography>
              <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 3 }}>
                We asked a simple question. The answer took two years.
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
                We started Smart Media Clarity because we were reading the same news stories that you were reading. A daycare worker in Texas who kicked toddlers 134 times over three months. A Minnesota infant who died because someone who had worked at the daycare for three weeks decided to seek attention by harming a baby. A university-run childcare center where four staff members — including the director — were arrested for abuse spanning four months.
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
                In every case, the cameras were running. The footage existed. And nothing happened until someone stumbled onto it — a coworker who happened to review a clip, a parent who complained about water-spraying, an intern who witnessed something directly.
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8 }}>
                We are technologists who build AI systems. We are also parents who drop our children off in the morning and trust strangers with them all day. Sitting with both of those facts at the same time is uncomfortable. Building something about it is better than not.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                {[
                  { num: '2+', label: 'Years building before our first deployment', detail: 'We researched, trained, and tested before we put the system in a real facility.' },
                  { num: '100+', label: 'Documented abuse cases studied', detail: 'Every detection model we built was trained against real patterns from real cases — not hypothetical scenarios.' },
                  { num: '0', label: 'Shortcuts taken on privacy', detail: 'Privacy architecture was built first. The product was built around it, not added to it afterward.' },
                ].map(({ num, label, detail }) => (
                  <Box key={num} sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.4rem', color: '#5ce1e6', lineHeight: 1, mb: 0.75 }}>{num}</Typography>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
            What We Believe
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', textAlign: 'center', mb: 1.5 }}>
            Six things we refuse to compromise on
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', textAlign: 'center', mb: 8, maxWidth: 500, mx: 'auto' }}>
            These are not marketing principles. They are the constraints we gave ourselves on day one and have not relaxed.
          </Typography>
          <Grid container spacing={2.5}>
            {values.map(({ icon, title, body, accent, border }) => (
              <Grid key={title} size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: accent, border: `1px solid ${border}`, borderRadius: 3, height: '100%' }}>
                  <Box sx={{ display: 'inline-flex', p: 1.25, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2, mb: 2 }}>{icon}</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
            Our Story
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 8 }}>
            Two years before we deployed a single camera
          </Typography>
          <Stack spacing={0}>
            {timeline.map(({ year, title, body }, i) => (
              <Box key={year} sx={{ display: 'flex', gap: { xs: 2, md: 4 }, pb: i < timeline.length - 1 ? 5 : 0 }}>
                {/* Year + line */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.04em' }}>{year}</Typography>
                  </Box>
                  {i < timeline.length - 1 && (
                    <Box sx={{ width: 1, flex: 1, bgcolor: 'rgba(92,225,230,0.12)', mt: 1 }} />
                  )}
                </Box>
                {/* Content */}
                <Box sx={{ pt: 0.75, pb: i < timeline.length - 1 ? 2 : 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 0.75 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Honest section */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
                Honest About What We Are
              </Typography>
              <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 3 }}>
                We are a startup. We move carefully anyway.
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
                We do not have hundreds of customers. We have a pilot program with limited spots, because we want every deployment to be right — not just running. We are onboarding facilities we can actually support, at a pace that lets us maintain quality.
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
                We are also honest that this technology is powerful and must be used responsibly. We do not sell to facilities with histories of violations and no plan to change. We complete a compliance checklist with every operator before the system goes live. We require written staff consent policies where state law requires them.
              </Typography>
              <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8 }}>
                We built SMCLA because we have children and because the technology existed and no one had built it right. We deploy it carefully because those same children are the reason it has to be.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                {[
                  { label: 'Pilot program', detail: 'Limited spots. Every new facility is reviewed before onboarding.' },
                  { label: 'Compliance-first', detail: 'State audio consent, DCFS requirements, and privacy compliance completed before go-live.' },
                  { label: 'No bulk surveillance', detail: 'Only flagged clips are retained. We do not build archives of your staff.' },
                  { label: 'Direct owner alerts', detail: 'Alerts go to the owner — not through a management chain that can minimize or suppress them.' },
                ].map(({ label, detail }) => (
                  <Box key={label} sx={{ display: 'flex', gap: 2, p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid rgba(92,225,230,0.5)', borderRadius: 2 }}>
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 6 }} />
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Talk to the people who built it
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', lineHeight: 1.7, mb: 4 }}>
            A demo with SMCLA is a conversation with the founders. No sales team. No scripts. If the system is right for your facility, we'll tell you. If it isn't, we'll tell you that too.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
              Book a Demo →
            </Button>
            <Button variant="outlined" onClick={() => navigate('/privacy')} sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 15, color: '#f0f0f8', borderColor: 'rgba(255,255,255,0.2)', borderRadius: 2, '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
              Read our privacy commitments
            </Button>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
